import { ReactNode, useState, useEffect } from "react";
import "./LeafPerfBonusScale.scss";
import React from "react";
import { IconCheckCircle } from "../../icons/IconCheckCircle/IconCheckCircle";
import { IconProgressMark } from "../../icons/IconProgressMark/IconProgressMark";
import { IconForward } from "../../icons/IconForward/IconForward";
import { IconBack } from "../../icons/IconBack/IconBack";
import { TrophyIcon } from "../../icons/IconTrophy/IconTrophy";
import { IconTargetBadge } from "../../icons/IconTargetBadge/IconTargetBadge";
import type { LegendItem } from "../../../services/MonthlyPerformanceIncome/monthlyPerformanceIncomeAPI.types";
import defaultContent from "../../../content/leafPerfBonusScale";
import { useTranslation } from "react-i18next";

type Tick = {
  value: number;
  label: string;
  gpvValue: number;
};
type Milestone = {
  value: number;
  achieved: boolean;
  id: string;
};

interface Props {
  max: number; // Maximum scale value
  progressValue: number; // Current progress position
  ticks: Tick[]; // Scale tick marks with labels
  milestones: Milestone[]; // Achievement milestones
  legends: LegendItem[]; // Legend items below timeline
  milestoneVariant?: "icon" | "checkCircle"; // "icon" = trophy / badge icons (default), "checkCircle" = green/gray check circles
  showMilestoneAtZero?: boolean; // When true, milestones with value=0 render at leftmost position (default: false)
  showProgressMarkAtZero?: boolean; // When true, progress mark shows at 0% when progressValue=0 (default: false)
}

export const LeafPerfBonusScale = ({
  max,
  progressValue,
  ticks,
  milestones,
  legends,
  milestoneVariant = "icon",
  showMilestoneAtZero = false,
  showProgressMarkAtZero = false,
}: Props) => {
  const { t } = useTranslation(["leafPerfBonusScale"]);
  const sortedTicks = ticks ?? [];

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();

    let rafId: number;
    const debouncedCheck = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(checkMobile);
    };

    window.addEventListener("resize", debouncedCheck);
    return () => {
      window.removeEventListener("resize", debouncedCheck);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const getTickIndexByGpvValue = (val: number): number => sortedTicks.findIndex((item) => item.gpvValue === val);

  const getTickIndexByValue = (val: number): number => sortedTicks.findIndex((item) => item.value === val);

  const maxIsPercentage = sortedTicks.some((tick) => tick.value === max);
  const maxTickIndex = maxIsPercentage ? getTickIndexByValue(max) : getTickIndexByGpvValue(max);

  const getScaledTickPosition = (percentValue: number, maxPercent: number): number => {
    if (percentValue === 0) return 0;
    if (percentValue >= maxPercent) return 100;

    // If there's only one tick or maxPercent is 0, use linear positioning
    if (maxPercent === 0 || percentValue === maxPercent) return 100;

    // Normalize the value to 0-1 range based on max
    const normalizedValue = percentValue / maxPercent;

    const scaledValue = Math.pow(normalizedValue, 1.5);

    // Round to 6 decimal places to avoid floating-point precision drift across environments
    return Math.round(scaledValue * 100 * 1e6) / 1e6;
  };

  const maxScaleVal = sortedTicks.length > 0 ? sortedTicks[sortedTicks.length - 1].gpvValue : 0;
  const maxShownValue = maxTickIndex !== -1 ? sortedTicks[maxTickIndex].gpvValue : maxScaleVal;

  // Count ALL unreached ticks/targets beyond current progress
  const unreachedTicksCount = sortedTicks.filter((tick) => tick.gpvValue > progressValue).length;

  // Count visible unreached ticks
  const visibleUnreachedCount =
    maxTickIndex !== -1
      ? sortedTicks.slice(0, maxTickIndex + 1).filter((tick) => tick.gpvValue > progressValue).length
      : unreachedTicksCount;

  // Show chevron if there are hidden targets beyond what's currently visible
  const dynamic = visibleUnreachedCount < unreachedTicksCount;
  const [showPart, setShowPart] = useState(dynamic && maxShownValue < maxScaleVal);

  useEffect(() => {
    if (!dynamic) {
      setShowPart(false);
    }
  }, [dynamic]);

  // Reset chevron state to collapsed when data changes (period change)
  // Use stable dependencies to avoid resetting during user interaction
  useEffect(() => {
    setShowPart(dynamic && maxShownValue < maxScaleVal);
  }, [progressValue, max, ticks.length, dynamic, maxShownValue, maxScaleVal]);

  const maxVal = dynamic && showPart ? maxShownValue : maxScaleVal;
  const maxInd = getTickIndexByGpvValue(maxVal);

  // Check if all ticks have the same percentage value (edge case)
  const allSamePercentValue =
    sortedTicks.length > 1 &&
    maxInd >= 0 &&
    sortedTicks.slice(0, maxInd + 1).every((tick, _, arr) => tick.value === arr[0].value);

  // Get the max percentage value for scaling
  const maxPercentValue = maxInd >= 0 ? sortedTicks[maxInd].value : 100;

  // Helper function to get tick position - uses GPV-based positioning when all ticks have same percentage
  // On mobile: weighted spacing for percentages when expanded, proportional for numbers or when collapsed
  // On desktop: always proportional spacing
  const getTickPosition = (tick: Tick, index: number): number => {
    // Single tick case - always position at 100%
    if (sortedTicks.length === 1 || maxInd === 0) {
      return 100;
    }

    // Check if ticks are showing percentages or numbers
    const hasPercentage = sortedTicks[0].label.includes("%");

    // On mobile
    if (isMobile) {
      // Check if we're showing the full scale (expanded) or partial (collapsed)
      const isFullyExpanded = maxInd === sortedTicks.length - 1;

      // Only use weighted spacing for percentages when fully expanded
      if (isFullyExpanded && hasPercentage) {
        if (index === maxInd) {
          return 100;
        }

        const visibleTicks = sortedTicks.slice(0, maxInd + 1);
        const weights = visibleTicks.map((t) => {
          const labelValue = parseInt(t.label.replace(/[^\d]/g, ""));
          return labelValue < 10 ? 1 : 1.5;
        });

        // Calculate total weight excluding the last tick (since it's fixed at 100%)
        const totalWeight = weights.slice(0, -1).reduce((sum, w) => sum + w, 0);

        // Calculate position based on cumulative weight
        let cumulativeWeight = 0;
        for (let i = 0; i < index; i++) {
          cumulativeWeight += weights[i];
        }

        // Scale to 0-100 range, leaving room for the last tick at 100%
        return (cumulativeWeight / totalWeight) * 100;
      }

      // For numbers or collapsed view: use proportional positioning (same as desktop)
      if (allSamePercentValue) {
        const maxGpv = sortedTicks[maxInd].gpvValue;
        return getScaledTickPosition(tick.gpvValue, maxGpv);
      }
      return getScaledTickPosition(tick.value, maxPercentValue);
    }

    // Desktop: use proportional spacing with power 1.5
    if (allSamePercentValue) {
      // When all ticks have the same percentage value, use GPV-based positioning
      const maxGpv = sortedTicks[maxInd].gpvValue;
      return getScaledTickPosition(tick.gpvValue, maxGpv);
    }
    // Normal case: use percentage-based positioning
    return getScaledTickPosition(tick.value, maxPercentValue);
  };

  // Calculate progress position using scaled spacing
  const getProgressPosition = (): number => {
    if (sortedTicks.length === 0 || maxInd < 0) return 0;

    // Find which two ticks the progress value falls between
    let lowerIndex = 0;
    let upperIndex = maxInd;

    for (let i = 0; i <= maxInd; i++) {
      if (sortedTicks[i].gpvValue <= progressValue) {
        lowerIndex = i;
      }
      if (sortedTicks[i].gpvValue >= progressValue && upperIndex === maxInd) {
        upperIndex = i;
        break;
      }
    }

    // If progress is beyond the last tick
    if (progressValue >= sortedTicks[maxInd].gpvValue) {
      return 100;
    }

    // If progress is before the first tick
    if (progressValue <= sortedTicks[0].gpvValue) {
      const ratio = progressValue / sortedTicks[0].gpvValue;

      if (isMobile) {
        // Mobile: get the actual first tick position for interpolation
        const firstTickPos = getTickPosition(sortedTicks[0], 0);
        return ratio * firstTickPos;
      }

      // Desktop: proportional spacing
      if (allSamePercentValue) {
        const firstTickPos = getScaledTickPosition(sortedTicks[0].gpvValue, sortedTicks[maxInd].gpvValue);
        return ratio * firstTickPos;
      }
      const firstTickPos = getScaledTickPosition(sortedTicks[0].value, maxPercentValue);
      return ratio * firstTickPos;
    }

    // Interpolate between the two ticks
    const lowerValue = sortedTicks[lowerIndex].gpvValue;
    const upperValue = sortedTicks[upperIndex].gpvValue;

    let lowerPos: number;
    let upperPos: number;

    if (isMobile) {
      const hasPercentage = sortedTicks[0]?.label.includes("%");
      const isFullyExpanded = maxInd === sortedTicks.length - 1;

      if (isFullyExpanded && hasPercentage) {
        // Weight-based spacing — matches getTickPosition for % labels when fully expanded
        const visibleTicks = sortedTicks.slice(0, maxInd + 1);
        const weights = visibleTicks.map((t) => {
          const labelValue = parseInt(t.label.replace(/[^\d]/g, ""));
          return labelValue < 10 ? 1 : 1.5;
        });
        const totalWeight = weights.slice(0, -1).reduce((sum, w) => sum + w, 0);

        if (upperIndex === maxInd) {
          upperPos = 100;
        } else {
          let cumulativeUpper = 0;
          for (let i = 0; i < upperIndex; i++) {
            cumulativeUpper += weights[i];
          }
          upperPos = (cumulativeUpper / totalWeight) * 100;
        }

        if (lowerIndex === maxInd) {
          lowerPos = 100;
        } else {
          let cumulativeLower = 0;
          for (let i = 0; i < lowerIndex; i++) {
            cumulativeLower += weights[i];
          }
          lowerPos = (cumulativeLower / totalWeight) * 100;
        }
      } else {
        // Scaled positioning — matches getTickPosition for number labels or collapsed view
        if (allSamePercentValue) {
          const maxGpv = sortedTicks[maxInd].gpvValue;
          lowerPos = getScaledTickPosition(sortedTicks[lowerIndex].gpvValue, maxGpv);
          upperPos = upperIndex === maxInd ? 100 : getScaledTickPosition(sortedTicks[upperIndex].gpvValue, maxGpv);
        } else {
          lowerPos = getScaledTickPosition(sortedTicks[lowerIndex].value, maxPercentValue);
          upperPos =
            upperIndex === maxInd ? 100 : getScaledTickPosition(sortedTicks[upperIndex].value, maxPercentValue);
        }
      }
    } else {
      // Desktop: proportional spacing
      if (allSamePercentValue) {
        const maxGpv = sortedTicks[maxInd].gpvValue;
        lowerPos = getScaledTickPosition(sortedTicks[lowerIndex].gpvValue, maxGpv);
        upperPos = getScaledTickPosition(sortedTicks[upperIndex].gpvValue, maxGpv);
      } else {
        lowerPos = getScaledTickPosition(sortedTicks[lowerIndex].value, maxPercentValue);
        upperPos = getScaledTickPosition(sortedTicks[upperIndex].value, maxPercentValue);
      }
    }

    const ratio = upperValue === lowerValue ? 0 : (progressValue - lowerValue) / (upperValue - lowerValue);
    return lowerPos + ratio * (upperPos - lowerPos);
  };

  const progressPercent = getProgressPosition();

  const tickColor = (index: number): string => {
    if (sortedTicks[index].gpvValue < progressValue) return "transparent";
    return "white";
  };

  const toggleFullScale = () => {
    setShowPart((prev: boolean) => !prev);
  };

  const getIconById = (id: string): ReactNode => {
    // Tiered target badge icons (generic - for SSI V2 and other tiered incentives)
    if (id === "tier1" || id === "tier2" || id === "tier3") {
      return <IconTargetBadge tier={id} />;
    }

    // Legacy trophy icons (for Performance Income Dashboard)
    if (id === "sp") {
      return <TrophyIcon variant="silverProducer" />;
    }

    if (id === "maximizeLeadership") {
      return <TrophyIcon variant="maxLeadershipBonus" />;
    }

    if (id === "bbi") {
      return <TrophyIcon variant="bronzeBuilder" />;
    }

    if (id === "bfi") {
      return <TrophyIcon variant="bronzeFoundation" />;
    }
  };

  const renderLegend = () =>
    [...legends]
      .sort((a, b) => a.gpvValue - b.gpvValue)
      .map((legendItem: LegendItem, index: number) => {
        // Use leafPerfBonusScale content for default values
        const defaultValue = defaultContent.legend[legendItem.label] ?? legendItem.label;

        // Translate with interpolation params if provided
        const translatedLabel = legendItem.displayLabel
          ? String(t(`legend.${legendItem.label}`, defaultValue, legendItem.displayLabel))
          : String(t(`legend.${legendItem.label}`, defaultValue));

        return (
          <div key={"milestone" + index} className="LeafPerfBonusScale__legend-item">
            <div className="LeafPerfBonusScale__legend-item-icon">
              {legendItem.achieved ? (
                <IconCheckCircle size="sm" color="green" solidCheckCircle={true} />
              ) : (
                getIconById(legendItem.id)
              )}
            </div>
            <div className="LeafPerfBonusScale__legend-item-label">{translatedLabel}</div>
          </div>
        );
      });

  const renderMilestones = () =>
    milestones?.map((milestone: Milestone, index: number) => {
      const tickInd = getTickIndexByGpvValue(milestone.value);
      // When showMilestoneAtZero is true, milestones with value=0 render at the leftmost position (0%)
      const isAtZero = showMilestoneAtZero && milestone.value === 0;
      let position: number | null;
      if (isAtZero) {
        position = 0;
      } else if (tickInd === -1 || tickInd > maxInd) {
        position = null;
      } else {
        position = getTickPosition(sortedTicks[tickInd], tickInd);
      }
      if (position === null) return null;

      // Add modifier class for milestone at zero position (needs different transform)
      const className = isAtZero
        ? "LeafPerfBonusScale__milestone LeafPerfBonusScale__milestone--at-zero"
        : "LeafPerfBonusScale__milestone";

      return (
        <div
          key={"milestone" + index}
          className={className}
          style={{
            left: position + "%",
          }}
        >
          {milestone.achieved ? (
            <IconCheckCircle size="sm" color="green" solidCheckCircle={true} />
          ) : milestoneVariant === "checkCircle" ? (
            <IconCheckCircle size="sm" color="gray" solidCheckCircle={false} />
          ) : (
            getIconById(milestone.id)
          )}
        </div>
      );
    });

  const renderTicks = () =>
    sortedTicks?.map((tick: Tick, index: number) => {
      if (index > maxInd) return null;

      let label = tick.label;
      let includesPercent = false;

      const isSingleTick = maxInd === 0;
      const isLastTick = index === maxInd;

      if (tick.label.includes("%")) {
        label = tick.label.replace("%", "");
        includesPercent = true;
      }

      const tickPosition = getTickPosition(tick, index);

      // Left-align only when the tick is at the left edge (position ~0%) to prevent clipping.
      // Otherwise center it on the tick mark, including the first tick when it's not at the edge.
      let alignmentClass = "LeafPerfBonusScale__tick-number--middle";
      if (isSingleTick) {
        alignmentClass = "LeafPerfBonusScale__tick-number--first";
      } else if (index === 0 && tickPosition < 1) {
        alignmentClass = "LeafPerfBonusScale__tick-number--first";
      } else if (isLastTick) {
        alignmentClass = "LeafPerfBonusScale__tick-number--last";
      }

      return (
        <React.Fragment key={"tick" + index}>
          <div
            className="LeafPerfBonusScale__tick"
            style={{
              left: tickPosition + "%",
              background: tickColor(index),
            }}
          />
          <div
            className={`LeafPerfBonusScale__tick-number ${alignmentClass} ${
              isSingleTick ? "LeafPerfBonusScale__tick-number--single" : ""
            }`}
            style={{
              left: tickPosition + "%",
            }}
          >
            {label}
            {includesPercent && <span className="LeafPerfBonusScale__tick-percent">%</span>}
          </div>
        </React.Fragment>
      );
    });

  return (
    <div className="LeafPerfBonus">
      <div className={"LeafPerfBonusScale" + (showPart ? " LeafPerfBonusScale__part" : "")}>
        <div className="LeafPerfBonusScale__container">
          <div className="LeafPerfBonusScale__progress-container">
            <div
              className="LeafPerfBonusScale__progress-filled"
              style={{
                width: progressPercent + "%",
              }}
            ></div>
            {renderTicks()}
            {(progressValue > 0 || showProgressMarkAtZero) && (
              <div
                className={`LeafPerfBonusScale__progress-mark${showProgressMarkAtZero && progressValue === 0 ? " LeafPerfBonusScale__progress-mark--at-zero" : ""}`}
                style={{
                  left: progressPercent + "%",
                }}
              >
                <IconProgressMark />
              </div>
            )}
            {renderMilestones()}
          </div>
          <div className="LeafPerfBonusScale__edge"></div>
        </div>
        {dynamic && (
          <>
            {showPart ? (
              <div
                className="LeafPerfBonusScale__forward"
                role="button"
                tabIndex={0}
                aria-label={String(t("expandBonusScale", defaultContent.expandBonusScale || "Expand bonus scale"))}
                onClick={toggleFullScale}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleFullScale();
                  }
                }}
              >
                <IconForward />
              </div>
            ) : (
              <div
                className="LeafPerfBonusScale__back"
                role="button"
                tabIndex={0}
                aria-label={String(
                  t("collapseBonusScale", defaultContent.collapseBonusScale || "Collapse bonus scale"),
                )}
                onClick={toggleFullScale}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleFullScale();
                  }
                }}
              >
                <IconBack width={"5"} height={"7"} color={"#38539A"} />
              </div>
            )}
          </>
        )}
      </div>
      <div className="LeafPerfBonusScale__legend">{renderLegend()}</div>
    </div>
  );
};
